import { useId, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { EXAMPLE_PLATES } from '../domain/examplePlates'
import { formatPlateInput, parsePlate, PLATE_LENGTH } from '../domain/licensePlate'
import { Icon } from './Icon'
import { PlateBand } from './LicensePlate'

interface PlateSearchProps {
  /** `compact` is the header variant; `full` is the card on the home and report pages. */
  variant?: 'full' | 'compact'
  /** Prefills the box, so the report page can show which plate is being viewed. */
  initialValue?: string
  autoFocus?: boolean
  /**
   * Offers the development-only example plates below the box. Ignored in a production build, where
   * the list does not exist. Opt-in per instance so the page that has several search boxes does not
   * repeat it under each one.
   */
  examples?: boolean
}

/**
 * Groups the plate in the field itself, and holds the page still while doing it.
 *
 * Writing the masked value into the DOM node during `onChange`, before React commits, is what stops
 * React rewriting the input and restoring the caret afterwards: the node and the state it is about
 * to be given already agree, so there is nothing for React to do.
 *
 * The scroll restore is belt-and-braces against a known trap.
 * Moving the caret in a focused input makes a browser scroll it into view, and the site header is
 * `position: sticky`, so anything that reserves space at the top of the scrollport leaves the
 * header's own contents looking partly hidden and starts a scroll on every keystroke. See the note
 * in `base.css`, which is where that is prevented. This costs nothing when the page has not moved:
 * it runs only when the mask changes something, acts only if the position really shifted, and uses
 * `instant` so `scroll-behavior: smooth` cannot animate the correction.
 */
function applyMask(node: HTMLInputElement): string {
  const formatted = formatPlateInput(node.value)

  if (node.value === formatted) {
    return formatted
  }

  const left = window.scrollX
  const top = window.scrollY

  node.value = formatted

  const restore = () => {
    if (window.scrollX !== left || window.scrollY !== top) {
      window.scrollTo({ left, top, behavior: 'instant' })
    }
  }

  restore()
  requestAnimationFrame(restore)

  return formatted
}

/**
 * The license plate search.
 *
 * Validation happens here against the same sidecode list the API uses, so an obvious typo never
 * costs a round trip — but a plate that passes is still only a candidate: the API re-validates it
 * and remains the authority on what exists.
 */
export function PlateSearch({
  variant = 'full',
  initialValue = '',
  autoFocus = false,
  examples = false,
}: PlateSearchProps) {
  const navigate = useNavigate()
  const inputId = useId()
  const errorId = useId()
  // The grouped form, dashes and all, so the state and the field always hold the same string. See
  // the note on `onChange` for why that matters more than it looks like it should.
  const [value, setValue] = useState(() => formatPlateInput(initialValue))
  const [error, setError] = useState<string>()

  function handleSubmit(event: FormEvent) {
    event.preventDefault()

    const plate = parsePlate(value)
    if (!plate) {
      setError(
        value.length === 0
          ? 'Vul een kenteken in.'
          : 'Dit is geen geldig Nederlands kenteken.',
      )
      return
    }

    setError(undefined)
    navigate(`/kenteken/${plate.compact}`)
  }

  return (
    <form
      className={`plate-search${variant === 'compact' ? ' plate-search--compact' : ''}`}
      onSubmit={handleSubmit}
      role="search"
    >
      <label className="visually-hidden" htmlFor={inputId}>
        Kenteken
      </label>

      <div className="plate-search__field">
        <PlateBand className="plate-search__band" />
        <input
          id={inputId}
          className="plate-search__input"
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          // The longest plate is six characters, but the dashes this adds need room too.
          maxLength={PLATE_LENGTH + 2}
          placeholder="XX-123-X"
          autoFocus={autoFocus}
          value={value}
          aria-invalid={error !== undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => {
            const formatted = applyMask(event.target)
            setValue(formatted)
            setError(undefined)
          }}
        />
      </div>

      <button
        className={variant === 'compact' ? 'button button--search' : 'button button--block'}
        type="submit"
      >
        <Icon name="search" size={variant === 'compact' ? 16 : 18} />
        {variant === 'compact' ? <span className="visually-hidden">Zoek kenteken</span> : 'Bekijk het rapport'}
      </button>

      {error && (
        <p className="plate-search__error" id={errorId} role="alert">
          {error}
        </p>
      )}

      {/*
        Development only. `import.meta.env.DEV` is replaced with a literal at build time, so the
        whole block — and the import feeding it — is dropped from the production bundle rather than
        merely hidden. Visitors never see these; they are real people's cars.
      */}
      {import.meta.env.DEV && examples && variant === 'full' && (
        <div className="plate-search__examples">
          <p className="plate-search__examples-title">Voorbeelden (alleen in ontwikkeling)</p>
          <ul>
            {EXAMPLE_PLATES.map((example) => (
              <li key={example.plate}>
                <button type="button" onClick={() => navigate(`/kenteken/${example.plate}`)}>
                  <span className="plate-search__examples-plate">{example.plate}</span>
                  <span className="plate-search__examples-car">{example.car}</span>
                  <span className="plate-search__examples-note">{example.exercises}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </form>
  )
}
